/**
 * Modal to add/edit one day's working hours.
 * Total working time = (end − start) minus break; break is excluded from total. Times in AM/PM.
 */
import React, { useState, useEffect } from 'react';
import { formatHHmmToAmPm } from '../utils/formatTime';

const MAX_HOURS_PER_DAY = 24;

function hoursToHHmm(hours) {
  if (hours == null || Number.isNaN(hours)) return '';
  const capped = hours < 0 ? 0 : hours >= 24 ? 23 + 59 / 60 : hours;
  const h = Math.floor(capped);
  const m = Math.round((capped - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function parseHHmm(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':').map(Number);
  if (parts.length >= 2) return parts[0] + parts[1] / 60;
  return null;
}

function parseHHmmToHours(str) {
  if (!str || typeof str !== 'string') return null;
  const parts = str.trim().split(':').map(Number);
  if (parts.length < 2) return null;
  return parts[0] + (parts[1] || 0) / 60;
}

/** Format break with AM/PM: "12:00 PM – 1:00 PM" when we have times, else duration "1h 0m".
 *  Returns null if the break falls outside (or exactly at the end of) working hours. */
function formatBreakDisplay(entry) {
  const bs = entry?.breakStartTime?.trim();
  const be = entry?.breakEndTime?.trim();
  const workEnd = entry?.endTime?.trim();
  if (bs && be) {
    // A break that starts at or after work ends is invalid – don't show it.
    const bsH = parseHHmmToHours(bs);
    const weH = parseHHmmToHours(workEnd);
    if (bsH != null && weH != null && bsH >= weH) return null;
    return `${formatHHmmToAmPm(bs)} – ${formatHHmmToAmPm(be)}`;
  }
  const startTime = entry?.startTime ?? null;
  const endTime = entry?.endTime ?? null;
  const totalHours = entry?.totalHours != null ? Number(entry.totalHours) : 0;
  const s = parseHHmmToHours(startTime);
  const e = parseHHmmToHours(endTime);
  if (s == null || e == null || e <= s || totalHours < 0) return null;
  const span = e - s;
  const breakH = Math.round((span - totalHours) * 100) / 100;
  if (breakH <= 0) return null;
  const h = Math.floor(breakH);
  const m = Math.round((breakH - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** Break duration in hours (0 if no break). */
function getBreakHours(breakStartTime, breakEndTime) {
  const bs = parseHHmmToHours(breakStartTime);
  const be = parseHHmmToHours(breakEndTime);
  if (bs == null || be == null || be <= bs) return 0;
  return be - bs;
}

/** Working time (span minus break). Break is only counted if it falls within working hours. */
function getNetTotalHours(startTime, endTime, breakStartTime, breakEndTime) {
  const s = parseHHmmToHours(startTime);
  const e = parseHHmmToHours(endTime);
  if (s == null || e == null || e <= s) return null;
  const span = e - s;
  // Ignore a break that starts at or after the work end time.
  const bsH = parseHHmmToHours(breakStartTime);
  const effectiveBreakH = (bsH != null && bsH >= e) ? 0 : getBreakHours(breakStartTime, breakEndTime);
  return Math.round(Math.max(0, span - effectiveBreakH) * 100) / 100;
}

function formatTotalTimeDisplay(startTime, endTime, breakStartTime, breakEndTime) {
  const net = getNetTotalHours(startTime, endTime, breakStartTime, breakEndTime);
  if (net == null) return null;
  const h = Math.floor(net);
  const m = Math.round((net - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function DayEntryModal({ date, entry, defaults, onSave, onDelete, onClose, saving, isDateInApprovedLeave = false, readOnly = false }) {
  const defaultStart = defaults?.defaultStartTime || '09:00';
  const defaultEnd = defaults?.defaultEndTime || '17:00';
  const defaultTotal = defaults?.defaultTotalHours != null ? String(defaults.defaultTotalHours) : '8';

  const [startTime, setStartTime] = useState(entry?.startTime || defaultStart);
  const [endTime, setEndTime] = useState(entry?.endTime || defaultEnd);
  const defaultBreakStart = defaults?.breakStartTime ?? '';
  const defaultBreakEnd = defaults?.breakEndTime ?? '';
  // For existing entries never fall back to defaults – the stored break (or absence of one) is authoritative.
  const [breakStartTime, setBreakStartTime] = useState(
    entry ? (entry.breakStartTime ?? '') : defaultBreakStart
  );
  const [breakEndTime, setBreakEndTime] = useState(
    entry ? (entry.breakEndTime ?? '') : defaultBreakEnd
  );
  const [totalHours, setTotalHours] = useState(
    entry?.totalHours != null ? String(entry.totalHours) : defaultTotal
  );
  const [note, setNote] = useState(entry?.note ?? '');
  const [validationError, setValidationError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const showBreak = !entry || entry.entryMode === 'time';
  const entryWithBreakTimes = entry
    ? { ...entry, startTime: entry.startTime ?? startTime, endTime: entry.endTime ?? endTime, totalHours: entry.totalHours ?? totalHours, breakStartTime: breakStartTime || entry.breakStartTime, breakEndTime: breakEndTime || entry.breakEndTime }
    : { startTime, endTime, totalHours, breakStartTime, breakEndTime };
  const breakDisplay = showBreak ? formatBreakDisplay(entryWithBreakTimes) : null;
  const timeRangeDisplay = startTime && endTime ? `${formatHHmmToAmPm(startTime)} – ${formatHHmmToAmPm(endTime)}` : null;
  const totalTimeDisplay = formatTotalTimeDisplay(startTime, endTime, breakStartTime, breakEndTime);

  useEffect(() => {
    setNote(entry?.note ?? '');
  }, [entry?.note]);

  useEffect(() => {
    if (entry) {
      // Editing/viewing an existing entry: use only what is stored, never override with defaults.
      setBreakStartTime(entry.breakStartTime ?? '');
      setBreakEndTime(entry.breakEndTime ?? '');
    } else {
      // New entry: seed from defaults.
      setBreakStartTime(defaults?.breakStartTime ?? '');
      setBreakEndTime(defaults?.breakEndTime ?? '');
    }
  }, [entry?.breakStartTime, entry?.breakEndTime, defaults?.breakStartTime, defaults?.breakEndTime]);

  useEffect(() => {
    const net = getNetTotalHours(startTime, endTime, breakStartTime, breakEndTime);
    if (net != null) {
      setTotalHours(String(net));
    }
  }, [startTime, endTime, breakStartTime, breakEndTime]);

  const handleTotalChange = (e) => {
    const value = e.target.value;
    setTotalHours(value);
    const s = parseHHmm(startTime);
    const t = parseFloat(value, 10);
    const breakH = getBreakHours(breakStartTime, breakEndTime);
    if (s != null && !Number.isNaN(t) && t >= 0) {
      setEndTime(hoursToHHmm(s + t + breakH));
    }
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    setValidationError('');
    const s = parseHHmm(startTime);
    const endVal = parseHHmm(endTime);
    if (s == null || endVal == null) {
      setValidationError('Enter valid times (HH:mm)');
      return;
    }
    if (endVal <= s) {
      setValidationError('End time must be after start time');
      return;
    }
    const breakH = getBreakHours(breakStartTime, breakEndTime);
    const totalWorking = Math.round(Math.max(0, (endVal - s) - breakH) * 100) / 100;
    if (totalWorking < 0) {
      setValidationError('End time must be after start time (after break)');
      return;
    }
    if (totalWorking > MAX_HOURS_PER_DAY) {
      setValidationError(`Total working time cannot exceed ${MAX_HOURS_PER_DAY}h`);
      return;
    }
    const bs = breakStartTime?.trim() || null;
    const be = breakEndTime?.trim() || null;
    onSave({
      date,
      entryMode: 'time',
      startTime: startTime.trim(),
      endTime: endTime.trim(),
      totalHours: totalWorking,
      breakStartTime: bs,
      breakEndTime: be,
      note: note.trim() || null,
    });
  };

  const handleDelete = () => {
    if (!entry?._id || !onDelete) return;
    if (!window.confirm('Remove this day\'s entry?')) return;
    setDeleting(true);
    onDelete(entry._id);
  };

  const lastEdited = entry?.updatedAt
    ? new Date(entry.updatedAt).toLocaleString('default', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  const displayDate = date
    ? new Date(date + 'T12:00:00').toLocaleDateString('default', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

  return (
    <div className="wh-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`wh-modal ${readOnly ? 'wh-modal-readonly' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="day-entry-title"
      >
        <div className="wh-modal-header">
          <h3 id="day-entry-title">{readOnly ? 'View' : 'Working hours'} – {displayDate}</h3>
          <button
            type="button"
            className="btn btn-link wh-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="wh-modal-body">
          {isDateInApprovedLeave && (
            <div className="alert alert-warning py-2 mb-3" role="alert">
              <strong>Conflict:</strong> This day is in your approved leave. You cannot be on leave and log working hours on the same day. Remove these hours or update your leave request.
            </div>
          )}
          <div className="wh-modal-times mb-3">
            {readOnly ? (
              <>
                {timeRangeDisplay && (
                  <div className="wh-modal-time-row mb-2">
                    <span className="wh-modal-time-label">Time</span>
                    <span className="wh-modal-time-value">{timeRangeDisplay}</span>
                  </div>
                )}
                {breakDisplay != null && (
                  <div className="wh-modal-time-row mb-2">
                    <span className="wh-modal-time-label">Break</span>
                    <span className="wh-modal-time-value">{breakDisplay}</span>
                  </div>
                )}
                {totalTimeDisplay != null && (
                  <div className="wh-modal-time-row mb-2">
                    <span className="wh-modal-time-label">Working time</span>
                    <span className="wh-modal-time-value">{totalTimeDisplay}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label small text-muted">Start</label>
                    <input
                      type="time"
                      className="form-control"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      step="300"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">End</label>
                    <input
                      type="time"
                      className="form-control"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      step="300"
                    />
                  </div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-6">
                    <label className="form-label small text-muted">Break start</label>
                    <input
                      type="time"
                      className="form-control"
                      value={breakStartTime}
                      onChange={(e) => setBreakStartTime(e.target.value)}
                      step="300"
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small text-muted">Break end</label>
                    <input
                      type="time"
                      className="form-control"
                      value={breakEndTime}
                      onChange={(e) => setBreakEndTime(e.target.value)}
                      step="300"
                    />
                  </div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <label className="form-label small text-muted">Total working time</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max={MAX_HOURS_PER_DAY}
                      step="any"
                      value={totalHours}
                      onChange={handleTotalChange}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="mb-2">
              <label className="form-label small text-muted">Notes <span className="fw-normal">(optional)</span></label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="e.g. Late start, overtime, WFH..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                readOnly={readOnly}
                disabled={readOnly}
              />
              {!readOnly && note.length > 0 && (
                <small className="text-muted">{note.length}/500</small>
              )}
            </div>
          </div>

          {lastEdited && (
            <p className="small text-muted mb-2">Last edited: {lastEdited}</p>
          )}

          {validationError && !readOnly && (
            <div className="alert alert-danger py-2 mb-3">{validationError}</div>
          )}

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              {!readOnly && entry?._id && onDelete && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleDelete}
                  disabled={saving || deleting}
                >
                  {deleting ? 'Removing...' : 'Remove entry'}
                </button>
              )}
            </div>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                {readOnly ? 'Close' : 'Cancel'}
              </button>
              {!readOnly && (
                <button type="submit" className="btn btn-warning" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DayEntryModal;
